import React, { useMemo } from "react";

import styles from "../styles/styles.module.css";
import { useChartContext } from "../core/ChartContext";
import {
  DEFAULT_CHART_COLOR,
  generateAreaPath,
  generateLinePath,
  getDirectionLineColor,
  getPointValue,
  getPriceDirection,
  GRADIENT_COLORS,
} from "~utils/ticker-chart";
import { useChartSubscription, useStoreRef } from "~hooks/ticker-chart";

export interface SingleColorLineProps {
  readonly seriesId: string;
  readonly color?: string;
  readonly strokeWidth?: number;
  readonly showGradient?: boolean;
}

const SingleColorLineInner: React.FC<SingleColorLineProps> = ({
  seriesId,
  color,
  strokeWidth = 2,
  showGradient = false,
}) => {
  const store = useChartContext();
  const storeRef = useStoreRef(store);

  const revision = useChartSubscription(store, ["data", "scales"]);

  return useMemo(() => {
    const renderableData = storeRef.current.getRenderableData(seriesId);
    const visibleData = storeRef.current.getVisibleData(seriesId);
    const scales = storeRef.current.getScales();
    const layout = storeRef.current.getLayout();
    const state = storeRef.current.getState();
    const chartMode = state.mode;

    if (renderableData.length === 0) return null;

    const isComparisonMode = chartMode === "comparison";
    const effectiveShowGradient = isComparisonMode ? false : showGradient;

    const seriesMeta = state.seriesMeta.get(seriesId);

    let effectiveLineColor: string;

    if (isComparisonMode) {
      effectiveLineColor = seriesMeta?.color || color || DEFAULT_CHART_COLOR;
    } else {
      const lastPoint = visibleData.at(-1);
      const lastValue = lastPoint ? getPointValue(lastPoint) : state.baseValue;
      const direction = getPriceDirection(state.baseValue, lastValue);

      effectiveLineColor = getDirectionLineColor(direction);
    }

    const pathD = generateLinePath(renderableData, {
      xScale: scales.x,
      yScale: scales.y,
    });

    if (effectiveShowGradient && renderableData.length >= 1) {
      const lastPoint = visibleData.at(-1);
      const lastValue = lastPoint ? getPointValue(lastPoint) : state.baseValue;
      const gradientDirection = getPriceDirection(state.baseValue, lastValue);
      const colors = GRADIENT_COLORS[gradientDirection];
      const areaPathD = generateAreaPath(renderableData, {
        xScale: scales.x,
        yScale: scales.y,
        baselineY: layout.priceArea.y + layout.priceArea.height,
      });
      const uniqueGradientId = `price-gradient-${seriesId}`;

      return (
        <>
          <defs>
            <linearGradient
              id={uniqueGradientId}
              x1={0}
              y1={0}
              x2={0}
              y2={1}
              color={colors.line}
              data-component="price-movement-chart-series-gradient"
            >
              <stop offset="0%" stopColor="currentColor" stopOpacity={0.3} />
              <stop offset="74.24%" stopColor="currentColor" stopOpacity={0} />
            </linearGradient>
          </defs>
          <path
            className={styles.area}
            d={areaPathD}
            fill={`url(#${uniqueGradientId})`}
            stroke="none"
            data-component="price-movement-chart-series-gradient-path"
          />
          <path
            className={styles.line}
            d={pathD}
            stroke={colors.line}
            strokeWidth={strokeWidth}
            fill="none"
            vectorEffect="non-scaling-stroke"
            data-component="price-movement-chart-series-path"
          />
        </>
      );
    }

    return (
      <path
        className={styles.line}
        d={pathD}
        stroke={effectiveLineColor}
        strokeWidth={strokeWidth}
        fill="none"
        vectorEffect="non-scaling-stroke"
        data-component="price-movement-chart-series-path"
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, seriesId, color, strokeWidth, showGradient]);
};

export const SingleColorLine = React.memo(SingleColorLineInner);

SingleColorLine.displayName = "SingleColorLine";
